import { useEffect, useImperativeHandle, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import LineMinusIcon from 'LuxKit/Icons/line/LineMinusIcon'
import Input from '../../client/components/Common/Form/Input/Input'
import LineCheckIcon from '../Icons/line/LineCheckIcon'
import BodyText from '../Typography/BodyText'
import OnlyIfChildren from 'components/Common/OnlyIfChildren/OnlyIfChildren'
import focusStyle from 'styles/utils/focusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'

const Icon = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  right: 0;
  bottom: 0;
  transition: color 0.2s;
  color: transparent;
  pointer-events: none;
  transform: translate3d(-50%, -50%, 0);
  height: ${rem(20)};
  width: ${rem(20)};
`

const Circle = styled.div`
  position: relative;
  height: ${rem(24)};
  width: ${rem(24)};
  flex-shrink: 0;
`

const CheckboxWrapper = styled.label`
  display: flex;
  position: relative;

  > * + * {
    margin-left: ${rem(16)};
  }

  &.passive-indicator {
    pointer-events: none;
  }
`

const CheckboxElement = styled(Input)`
  appearance: none;
  margin: 0;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: border-color 0.2s, background-color 0.2s, color 0.2s;
  width: 100%;
  height: 100%;
  border-radius: ${props => props.theme.borderRadius.round};

  &:not(:disabled) {
    &:checked {
      border-color: ${props => props.theme.palette.brand.primary.normal};
      background-color: ${props => props.theme.palette.brand.primary.normal};

      & + ${Icon} {
        color: ${props => props.theme.palette.neutral.surface.darkContrast};
      }
    }

    &:focus-visible {
      ${focusStyle}

      &.dirty:invalid {
        outline-color: ${props => props.theme.palette.messaging.critical.normalForeground};
      }
    }

    ${mediaHoverable} {
      &:hover {
        border-color: ${props => props.theme.palette.brand.primary.normal};
      }
    }

    &.dirty:invalid {
      border-color: ${props => props.theme.palette.messaging.critical.normalForeground};
    }
  }

  &:disabled {
    border-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

    &:checked + ${Icon} {
      color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    }
  }
`

const TextElement = styled.span`
  display: flex;
  flex-direction: column;
`

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** A possible 3rd value for check boxes, this is "sort of checked". */
  indeterminate?: boolean
  /**
   * This is text that will appear below the main children with a different visual
   * look. Often used as additional descriptions to the main element.
   **/
  subText?: string
}

const CircleCheckboxInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    children,
    className,
    id,
    indeterminate,
    subText,
    ...inputProps
  } = props

  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isDirty, setDirty] = useState<boolean>(false)
  const isPassiveIndicator = inputProps.checked !== undefined &&
    inputProps.onChange === undefined &&
    inputProps.onClick === undefined

  useImperativeHandle(ref, () => inputRef.current!)
  useEffect(() => {
    // there's no corresponding react prop, have to set manually via ref
    if (inputRef.current && indeterminate) {
      inputRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  const hasChildren = React.Children.toArray(children).length > 0

  return (
    <CheckboxWrapper
      className={clsx(className, { 'passive-indicator': isPassiveIndicator })}
      htmlFor={id}
    >
      <Circle>
        <CheckboxElement
          {...inputProps}
          ref={inputRef}
          id={id}
          type="checkbox"
          onDirtyChange={setDirty}
          readOnly={inputProps.readOnly ?? isPassiveIndicator}
          tabIndex={inputProps.tabIndex ?? (isPassiveIndicator ? -1 : undefined)}
          aria-hidden={inputProps['aria-hidden'] ?? isPassiveIndicator}
          className={clsx(
            { dirty: isDirty },
          )}
        />
        {!indeterminate && <Icon as={LineCheckIcon} />}
        {indeterminate && <Icon as={LineMinusIcon} />}
      </Circle>
      <OnlyIfChildren as={TextElement}>
        {hasChildren && <BodyText variant="large" as="span">{children}</BodyText>}
        {subText && <BodyText variant="medium" colour="light-foreground-alt" as="span">{subText}</BodyText>}
      </OnlyIfChildren>
    </CheckboxWrapper>
  )
})

CircleCheckboxInput.displayName = 'CircleCheckboxInput'

export default CircleCheckboxInput
